import axios from 'axios'
import { ElMessage } from 'element-plus'
import { useAuthStore } from '@/stores/auth'
import router from '@/router'

// 创建axios实例
const request = axios.create({
  baseURL: '/api',  // 使用相对路径，让代理处理
  timeout: 50000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

// 重试配置
const retryConfig = {
  retries: 2,
  retryDelay: 1000,
  retryCondition: (error) => {
    // 网络错误或超时重试
    return !error.response || // No response means network error
           error.code === 'ECONNABORTED' ||
           (error.response && error.response.status >= 500)
  }
}

// 添加重试拦截器
request.interceptors.response.use(undefined, async (error) => {
  const { config } = error
  
  if (!config || !retryConfig.retryCondition(error)) {
    return Promise.reject(error)
  }

  config.__retryCount = config.__retryCount || 0

  if (config.__retryCount >= retryConfig.retries) {
    return Promise.reject(error)
  }

  config.__retryCount += 1

  // 等待重试延迟
  await new Promise(resolve => setTimeout(resolve, retryConfig.retryDelay))

  return request(config)
})

// 请求拦截器
request.interceptors.request.use(
  config => {
    const authStore = useAuthStore()
    if (authStore.token) {
      config.headers['Authorization'] = `Bearer ${authStore.token}`
    }
    return config
  },
  error => {
    console.error('Request error:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
request.interceptors.response.use(
  response => {
    return response
  },
  error => {
    console.error('Response error:', error)
    const authStore = useAuthStore()
    
    // 处理401错误（未认证）
    if (error.response?.status === 401) {
      authStore.clearUserInfo() // 清除用户信息
      router.push({
        path: '/login',
        query: { redirect: router.currentRoute.value.fullPath }
      })
    }
    
    // 处理403错误（无权限）
    if (error.response?.status === 403) {
      if (router.currentRoute.value.path.startsWith('/admin')) {
        router.push('/')
      }
    }

    // 处理404错误（资源未找到）
    if (error.response?.status === 404) {
      router.push('/404')
      return Promise.reject(error)
    }
    
    // 处理超时错误
    if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
      ElMessage.error('请求超时，请检查网络连接')
      return Promise.reject(error)
    }
    
    // 显示错误消息
    const errorMsg = error.response?.data?.detail || error.message || '请求失败'
    ElMessage.error(errorMsg)
    
    return Promise.reject(error)
  }
)

export default request